/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { Observable, Subject, fromEvent, interval, take } from 'rxjs';

import { DealerService } from './dealer.service';
import { demoEvents as commonDemoEvents, configuration, getDemoInitialState } from './demo.data';
import { configuration as baccaratConfiguration, demoEvents as baccaratEvents } from '../games/demo-data/demo.baccarat.data';
import { configuration as dragonConfiguration, demoEvents as dragonTigerEvents } from '../games/demo-data/demo.dragon-tiger.data';
import { configuration as openBaccaratConfiguration, demoEvents as openBaccaratEvents } from '../games/demo-data/demo.open-baccarat.data';
import {
    configuration as openDragonConfiguration,
    demoEvents as openDragonTigerEvents
} from '../games/demo-data/demo.open-dragon-tiger.data';
import {
    configuration as threeHeadedDragonConfiguration,
    demoEvents as threeHeadedDragonTigerEvents
} from '../games/demo-data/demo.three-headed-dragon.data';
import { DealerAction, FlagStateType, GameType, SupervisorAction } from '../models/communication/app-enums.model';
import { AppEventType, ChatMessageEvent } from '../models/communication/app-events.model';
import { AppState } from '../models/communication/app-state.model';
import { CardsChanged } from '../models/communication/result-change.model';
import { YEventPayload } from '../models/y-protocol.model';

@Injectable({
    providedIn: 'root'
})
export class DemoService extends DealerService {
    private static readonly eventEmitIntervalMs = 3000;
    private static readonly gameData = {
        [GameType.Baccarat]: { configuration: baccaratConfiguration, events: baccaratEvents },
        [GameType.DragonTiger]: { configuration: dragonConfiguration, events: dragonTigerEvents },
        [GameType.OpenBaccarat]: { configuration: openBaccaratConfiguration, events: openBaccaratEvents },
        [GameType.OpenDragonTiger]: { configuration: openDragonConfiguration, events: openDragonTigerEvents },
        [GameType.ThreeHeadedDragon]: { configuration: threeHeadedDragonConfiguration, events: threeHeadedDragonTigerEvents }
    };

    onlyNecessaryDemoEvents = commonDemoEvents.slice(0, 1);

    demoEvents = [...commonDemoEvents, ...DemoService.gameData[configuration.gameType].events];
    demoInitialState = getDemoInitialState(
        DemoService.gameData[configuration.gameType].configuration as Partial<AppState['configuration']>
    );

    keydown$ = fromEvent(window, 'keydown');

    private readonly eventMessages = new Subject<YEventPayload<AppEventType>>();

    private readonly automatic: boolean = false;
    private readonly logEvents: boolean = true;

    private eventIndex = 0;

    override start(): void {
        super.start();
        if (this.automatic) {
            this.startAutoEvents();
        } else {
            this.initKeyboardControl();
        }
    }

    override async getInitData(): Promise<AppState> {
        return Promise.resolve(this.demoInitialState);
    }

    override async sendGetChatMessagesRequest(): Promise<ChatMessageEvent[]> {
        return Promise.resolve(this.demoInitialState.chatMessages ?? []);
    }

    override async sendGetGamesRequest(): Promise<GameType[]> {
        return Promise.resolve(this.demoInitialState.games ?? []);
    }

    override getEventsObservable(): Observable<YEventPayload<AppEventType>> {
        return this.eventMessages.asObservable();
    }

    override async sendSupervisorActionRequest(action: SupervisorAction): Promise<void> {
        console.log('sendDealerActionRequest', action);
    }

    override async sendDealerActionRequest(action: DealerAction): Promise<void> {
        console.log('sendDealerActionRequest', action);
    }

    override async sendChangeGameRequest(gameType: GameType): Promise<void> {
        console.log('sendChangeGameRequest', gameType);
    }

    override async sendSetFlagStateRequest(flag: FlagStateType, state: boolean): Promise<void> {
        console.log('sendSetFlagStateRequest', flag, state);
    }

    override async sendResultChangeRequest(cards: CardsChanged): Promise<void> {
        console.log('Result change:');
        console.log(JSON.stringify(cards, null, 2));
    }

    startAutoEvents(): void {
        interval(DemoService.eventEmitIntervalMs)
            .pipe(take(this.demoEvents.length))
            .subscribe(() => {
                this.eventIndex++;
                this.emitNextEvents();
            });
    }

    initKeyboardControl(): void {
        this.keydown$.subscribe((event) => {
            if ((event as KeyboardEvent).key === 'ArrowRight') {
                this.eventIndex++;
            }

            if ((event as KeyboardEvent).key === 'ArrowLeft') {
                this.eventIndex--;
            }

            this.emitNextEvents();
        });
    }

    emitNextEvents(): void {
        const nextEventOrEvents = this.demoEvents[this.eventIndex % this.demoEvents.length];
        // If you want to show specific phase only, use the following line instead of the above line
        // ...demoEvents[3 % demoEvents.length],
        const normalizedEvents = Array.isArray(nextEventOrEvents) ? nextEventOrEvents : [nextEventOrEvents];

        for (const normalizedEvent of normalizedEvents) {
            const message = {
                ...normalizedEvent,
                triggered: Date.now()
            };

            if (this.logEvents) {
                this.log(message);
            }

            setTimeout(() => {
                this.eventMessages.next(message);
            });
        }
    }

    log(message: object): void {
        console.log(JSON.stringify(message, null, 2));
    }
}
